import {sliderMoveDirections} from "@SearchResults/features/dateSlick/constants/sliderMoveDirections";
import DaysLimitType from "@SearchResults/features/dateSlick/types/DaysLimit.type";
import {useEffect, useRef, useState} from "react";
import useLimitSlidesToShow from "@SearchResults/features/dateSlick/hooks/useLimitSlidesToShow";
import createNewSliderDates from "@SearchResults/features/dateSlick/functions/createNewSliderDates";

interface UseMoveSliderAttributes {
    daysLimit: DaysLimitType,
    setDaysLimit: (value: DaysLimitType) => void,
    setDatesInSlider: (dates: Date[]) => void,
    centralDate: Date | string,
}

/**
 * Move entire slider left or right.
 */
export default function useMoveSlider({daysLimit, setDaysLimit, setDatesInSlider, centralDate}: UseMoveSliderAttributes) {
    const datesInSliderLimit = useLimitSlidesToShow()
    const sliderRef = useRef(null)
    const centralDateRef = useRef(null)
    const [displayLeftArrow, setDisplayLeftArrow] = useState(false)

    useEffect(() => {
        setDatesInSlider(createNewSliderDates(centralDate, daysLimit))
        if (sliderRef?.current && centralDateRef?.current) {
            const centralPosition = centralDateRef.current.offsetLeft - sliderRef.current.clientWidth / 2 + centralDateRef.current.clientWidth / 2
            if (centralPosition > 0) {
                setDisplayLeftArrow(true)
            } else {
                setDisplayLeftArrow(false)
            }
            sliderRef.current.scrollTo({
                left: centralPosition,
                behavior: "smooth",
            })
        }
    }, [centralDate])

    function moveSlider(direction) {
        if (![sliderMoveDirections.left, sliderMoveDirections.right].includes(direction)) {
            return null;
        }

        if (!sliderRef?.current) {
            return null;
        }

        let minDayLimit = daysLimit.min;
        let maxDayLimit = daysLimit.max;
        let diff = 0

        if (sliderMoveDirections.left === direction) {
            minDayLimit -= 1;
            diff = -sliderRef.current.clientWidth / datesInSliderLimit
        }

        if (sliderMoveDirections.right === direction) {
            maxDayLimit += 1;
            diff = sliderRef.current.clientWidth / datesInSliderLimit
        }

        // display left arrow only if some dated stay hidden under left side, if scroll on the left side is greater than 0 (using 10 for error margin)
        if (sliderRef.current.scrollLeft + diff <= 10) {
            setDisplayLeftArrow(false)
        } else {
            setDisplayLeftArrow(true)
        }

        sliderRef.current.scrollBy({
            left: diff,
            behavior: "smooth",
        })

        setDaysLimit({
            min: minDayLimit,
            max: maxDayLimit,
        })
        setDatesInSlider(createNewSliderDates(centralDate, {
            min: minDayLimit,
            max: maxDayLimit,
        }))
    }

    const moveSliderLeft = () => {
        moveSlider(sliderMoveDirections.left)
    }

    const moveSliderRight = () => {
        moveSlider(sliderMoveDirections.right)
    }

    return {
        moveSliderLeft,
        moveSliderRight,
        sliderRef,
        centralDateRef,
        displayLeftArrow,
    }
}